import {
  ApprovingInvoiceReportFormInputResponse,
  ApprovingInvoiceReportPaymentInfoResponse,
  reportFormInputType,
} from 'ap-openapi';
import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ProcessedBusinessDocument } from '../helpers';
import { useBusinessDocuments } from '../hooks/useBusinessDocuments';
import { ActiveFileId } from './PaymentDetailContext';

type BusinessDocumentsContextType = {
  data: ApprovingInvoiceReportPaymentInfoResponse | undefined;
  setData: (
    data: ApprovingInvoiceReportPaymentInfoResponse | undefined
  ) => void;
  processedDocuments: ProcessedBusinessDocument[];
  activeFile: ActiveFileId;
  selectDocumentFile: (document: ProcessedBusinessDocument) => () => void;
  isVisible: (item: ApprovingInvoiceReportFormInputResponse) => boolean;
};

const Context = createContext<BusinessDocumentsContextType>({
  data: undefined,
  setData: () => {},
  processedDocuments: [],
  activeFile: null,
  selectDocumentFile: () => () => {},
  isVisible: () => true,
});

export const BusinessDocumentsProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const baseURL = location.origin;
  const [data, setData] = useState<
    ApprovingInvoiceReportPaymentInfoResponse | undefined
  >(undefined);

  // Find the business documents field
  const businessDocsField = useMemo(
    () =>
      data?.report_form_inputs?.find(
        (item) =>
          item?.type ===
          reportFormInputType.ApReportFormInputBusinessDocumentsField
      ),
    [data?.report_form_inputs]
  );

  // Get processed documents
  const { processedDocuments, activeFile, selectDocumentFile } =
    useBusinessDocuments(businessDocsField, baseURL);

  const value = useMemo(
    () => ({
      data,
      setData,
      processedDocuments,
      activeFile,
      selectDocumentFile,
      isVisible: (item: ApprovingInvoiceReportFormInputResponse) =>
        !(
          item?.type ===
            reportFormInputType.ApReportFormInputBusinessDocumentsField &&
          !processedDocuments?.length
        ),
    }),
    [data, processedDocuments, activeFile, selectDocumentFile]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useBusinessDocumentsContext = () => useContext(Context);
